import * as THREE from 'three';
import type { InputController } from '../core/InputController';

export class CameraRig {
  private readonly desiredPosition = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private readonly desiredLookTarget = new THREE.Vector3();
  private readonly orbitInput = new THREE.Vector2();
  private readonly spherical = new THREE.Spherical();
  private readonly forwardScratch = new THREE.Vector3();
  // Start with the camera south of the elephant so the shade tree is a landmark,
  // not an occluder between the camera and the player.
  private yaw = 0;
  private pitch = 0.98;
  private distance = window.matchMedia('(pointer: coarse)').matches ? 15.5 : 13.5;
  private targetDistance = this.distance;
  private trauma = 0;
  private shakeTime = 0;
  private readonly baseFov: number;
  private fovPunch = 0;
  private reducedMotion = false;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    this.baseFov = camera.fov;
  }

  snapTo(target: THREE.Vector3): void {
    this.buildDesiredPosition(target);
    this.camera.position.copy(this.desiredPosition);
    this.lookTarget.copy(target);
    this.lookTarget.y += 1.45;
    this.camera.lookAt(this.lookTarget);
  }

  update(delta: number, target: THREE.Vector3, lag: number, input?: InputController): void {
    if (input) {
      input.consumeOrbit(this.orbitInput);
      this.yaw -= this.orbitInput.x * 0.0045;
      this.pitch = THREE.MathUtils.clamp(this.pitch + this.orbitInput.y * 0.0045, 0.3, 1.25);
      this.targetDistance = THREE.MathUtils.clamp(
        this.targetDistance + input.consumeZoom() * 0.009,
        6.5,
        18,
      );
    }

    this.distance = THREE.MathUtils.lerp(
      this.distance,
      this.targetDistance,
      1 - Math.exp(-delta * 8),
    );
    this.buildDesiredPosition(target);
    this.camera.position.lerp(
      this.desiredPosition,
      1 - Math.exp(-delta / Math.max(0.001, lag)),
    );
    this.desiredLookTarget.copy(target).addScaledVector(this.getForward(this.forwardScratch), 1.1);
    this.desiredLookTarget.y += 1.45;
    this.lookTarget.lerp(this.desiredLookTarget, 1 - Math.exp(-delta * 10));
    this.camera.lookAt(this.lookTarget);
    this.updateFeedback(delta);
  }

  getForward(target: THREE.Vector3): THREE.Vector3 {
    return target.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw)).normalize();
  }

  getRight(target: THREE.Vector3): THREE.Vector3 {
    return target.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw)).normalize();
  }

  addTrauma(amount: number): void {
    if (!this.reducedMotion) this.trauma = Math.min(1, this.trauma + amount);
  }

  punchFov(degrees: number): void {
    if (!this.reducedMotion) this.fovPunch = Math.min(9, this.fovPunch + degrees);
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
    if (enabled) {
      this.trauma = 0;
      this.fovPunch = 0;
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
  }

  diagnostics(): {
    yaw: number;
    pitch: number;
    distance: number;
    position: { x: number; y: number; z: number };
  } {
    return {
      yaw: this.yaw,
      pitch: this.pitch,
      distance: this.distance,
      position: {
        x: this.camera.position.x,
        y: this.camera.position.y,
        z: this.camera.position.z,
      },
    };
  }

  private buildDesiredPosition(target: THREE.Vector3): void {
    this.spherical.set(this.distance, this.pitch, this.yaw);
    this.desiredPosition.setFromSpherical(this.spherical).add(target);
    this.desiredPosition.y += 1.5;
  }

  private updateFeedback(delta: number): void {
    this.shakeTime += delta;
    this.trauma = Math.max(0, this.trauma - delta * 1.45);
    if (this.trauma > 0) {
      const shake = this.trauma * this.trauma;
      const frequency = this.shakeTime * 34;
      this.camera.position.x += this.noise(frequency, 1) * 0.32 * shake;
      this.camera.position.y += this.noise(frequency, 2) * 0.22 * shake;
      this.camera.rotation.z += this.noise(frequency, 3) * 0.035 * shake;
    }

    this.fovPunch *= Math.exp(-delta / 0.22);
    if (this.fovPunch < 0.001) this.fovPunch = 0;
    const nextFov = this.baseFov + this.fovPunch;
    if (Math.abs(this.camera.fov - nextFov) > 0.001) {
      this.camera.fov = nextFov;
      this.camera.updateProjectionMatrix();
    }
  }

  private noise(time: number, seed: number): number {
    const value = Math.sin(time * 12.9898 + seed * 78.233) * 43758.5453;
    return (value - Math.floor(value)) * 2 - 1;
  }
}
